import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { BranchProvider } from './context/BranchContext';
import { TenantBrandingProvider } from './context/TenantBrandingContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { SuperAdminProvider, useSuperAdmin } from './context/SuperAdminContext';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import TenantsList from './pages/admin/TenantsList';
import TenantDetails from './pages/admin/TenantDetails';
import PlanManagement from './pages/admin/PlanManagement';
import SubscriptionsList from './pages/admin/SubscriptionsList';
import PaymentHistory from './pages/admin/PaymentHistory';
import PaymentApprovals from './pages/admin/PaymentApprovals';
import DiscountCodes from './pages/admin/DiscountCodes';
import AuditLog from './pages/admin/AuditLog';
import ZatcaOverview from './pages/admin/ZatcaOverview';
import AdminsManagement from './pages/admin/AdminsManagement';
import TemplateManagement from './pages/admin/TemplateManagement';

// Components
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import FeatureGate from './components/FeatureGate';

// Pages
import Login from './pages/Login';
import NewCustomer from './pages/customers/NewCustomer';
import CustomersList from './pages/customers/CustomersList';
import DevicesManagement from './pages/settings/DevicesManagement';
import AnalyzerMappings from './pages/settings/AnalyzerMappings';
import LabSettings from './pages/settings/LabSettings';
import BranchesManagement from './pages/settings/BranchesManagement';
import StaffManagement from './pages/settings/StaffManagement';
import ContractorLabsManagement from './pages/settings/ContractorLabsManagement';
import TestCategories from './pages/tests/TestCategories';
import AllTests from './pages/tests/AllTests';
import TestPackages from './pages/tests/TestPackages';
import MicrobiologyDictionary from './pages/tests/MicrobiologyDictionary';
import ContractedLabPrices from './pages/tests/ContractedLabPrices';
import TestPricing from './pages/tests/TestPricing';
import IncomeReport from './pages/reports/IncomeReport';
import DailyIncomeReport from './pages/reports/DailyIncomeReport';
import SalesAnalysis from './pages/reports/SalesAnalysis';
import ZatcaInvoiceLog from './pages/reports/ZatcaInvoiceLog';
import NewBooking from './pages/bookings/NewBooking';
import BookingDetails from './pages/bookings/BookingDetails';
import WorkRequest from './pages/bookings/WorkRequest';
import BookingsList from './pages/bookings/BookingsList';
import HomeVisitsList from './pages/bookings/HomeVisitsList';
import Invoice from './pages/bookings/Invoice';
import Coupons from './pages/marketing/Coupons';
import WhatsappMarketing from './pages/marketing/WhatsappMarketing';
import LabResults from './pages/lab/LabResults';
import ApproveResults from './pages/lab/ApproveResults';
import MedicalReport from './pages/lab/MedicalReport';
import QCMaterialsSetup from './pages/qc/QCMaterialsSetup';
import QCDailyEntry from './pages/qc/QCDailyEntry';
import QCDashboard from './pages/qc/QCDashboard';
import LabChat from './pages/lab/LabChat';
import ResultsAnalysis from './pages/lab/ResultsAnalysis';
import PatientProfileAnalysis from './pages/lab/PatientProfileAnalysis';

// Subscription Pages
import MySubscription from './pages/settings/MySubscription';
import SubscriptionChangeFlow from './pages/settings/SubscriptionChangeFlow';
import PaymentCallback from './pages/settings/PaymentCallback';
import SubscriptionExpired from './pages/settings/SubscriptionExpired';

// Website (per-tenant marketing site) Pages
import WebsiteBuilder from './pages/website/WebsiteBuilder';
import WebsiteRouter from './pages/website/WebsiteRouter';
import BlogManagement from './pages/website/BlogManagement';
import ContactInbox from './pages/website/ContactInbox';
import BlogPostEditor from './pages/website/BlogPostEditor';
import OfferManagement from './pages/website/OfferManagement';
import OfferEditor from './pages/website/OfferEditor';
import MediaLibrary from './pages/website/MediaLibrary';
import SEOSettings from './pages/website/SEOSettings';
import SiteSettings from './pages/website/SiteSettings';
import DomainManagement from './pages/website/DomainManagement';

// Landing Page
import LandingPage from './pages/landing/LandingPage';
import PricingPage from './pages/landing/PricingPage';
import AboutPage from './pages/landing/AboutPage';

// Onboarding Pages
import Register from './pages/onboarding/Register';
import SetupWizard from './pages/onboarding/SetupWizard';
import OnboardingFlow from './pages/onboarding/OnboardingFlow';

// Auth aux pages (magic-link + forgot/reset)
import AutoLogin from './pages/AutoLogin';
import ForceChangePassword from './pages/ForceChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Patient Portal Pages
import PatientLogin from './pages/patient/PatientLogin';
import PatientResults from './pages/patient/PatientResults';
import PatientQRLogin from './pages/patient/PatientQRLogin';

// Contractor Portal Pages
import ContractorLogin from './pages/contractor/ContractorLogin';
import ContractorDashboard from './pages/contractor/ContractorDashboard';
import ContractorStatement from './pages/contractor/ContractorStatement';
import ContractorAccounts from './pages/settings/ContractorAccounts';

// Contractor Bookings (Admin)
import ContractorBookingsList from './pages/bookings/ContractorBookingsList';

// Icons for placeholder pages
import { 
  CalendarCheck, FlaskConical, TestTube2, FileBarChart, 
  Megaphone, Settings, ClipboardList, Send, FileText, 
  MessageCircle, BarChart3, Package, DollarSign, Building2, ListTree, 
  HelpCircle, Percent, Star, MessageSquare, Wrench, UserCog, Cpu, 
  GitBranch, Shield, Link2
} from 'lucide-react';

// Placeholder component for pages not yet implemented
const PlaceholderPage = ({ title, icon: Icon, description }) => (
  <div className="container mx-auto px-6 py-12">
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-2xl mx-auto text-center"
    >
      <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
        <Icon className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-3xl font-bold text-gray-800 mb-3">{title}</h1>
      <p className="text-gray-500 text-lg mb-8">{description}</p>
      <div className="inline-flex items-center gap-2 px-6 py-3 bg-blue-50 text-blue-600 rounded-xl text-sm font-medium">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
        قيد التطوير
      </div>
    </motion.div>
  </div>
);

// Protected Route wrapper - checks authentication, page permissions, and subscription features
const ProtectedRoute = ({ children, pageKey, featureKey }) => {
  const { isAuthenticated, loading, canAccessPage } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force-change-password gate: if the server flagged this user, nothing
  // else on the dashboard is reachable until they pick a new password.
  // The flag flips to false inside /auth/set-initial-password or
  // /auth/change-password.
  try {
    const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (cachedUser?.must_change_password && window.location.pathname !== '/force-change-password') {
      return <Navigate to="/force-change-password" replace />;
    }
  } catch { /* ignore */ }

  // Bounce tenants that never finished checkout (or post-payment setup) back
  // into the onboarding flow. Reading from the tenant blob cached at
  // login/checkAuth — no extra round-trip needed.
  try {
    const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
    const status = tenant?.accountStatus;
    const step = tenant?.onboardingStep;
    const here = window.location.pathname;
    if (!here.startsWith('/onboarding')) {
      // Not active yet → must finish checkout flow
      if (status && status !== 'active') {
        return <Navigate to="/onboarding" replace />;
      }
      // Active but post-payment setup wizard not done → resume onboarding
      if (status === 'active' && step && step !== 'READY') {
        return <Navigate to="/onboarding" replace />;
      }
    }
  } catch { /* ignore parse errors — fall through */ }

  // Check page permission if pageKey is provided
  if (pageKey && !canAccessPage(pageKey)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3">غير مصرح</h1>
          <p className="text-gray-500 mb-6">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700"
          >
            العودة
          </button>
        </div>
      </div>
    );
  }

  // If featureKey provided, wrap with FeatureGate
  if (featureKey) {
    return <FeatureGate feature={featureKey}>{children}</FeatureGate>;
  }

  return children;
};

// Home Route - Dashboard for owner, or first allowed page for others
const HomeRoute = () => {
  const { user, loading, canAccessPage } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  // Owner sees Dashboard
  if (user?.role === 'owner') {
    return <Dashboard />;
  }

  // Check if user can access dashboard
  if (canAccessPage('dashboard')) {
    return <Dashboard />;
  }
  
  // Otherwise redirect to bookings
  return <Navigate to="/bookings" replace />;
};

// Layout with Navbar
const ExpiryBanner = React.lazy(() => import('./components/ExpiryBanner'));
const FreePlanBanner = React.lazy(() => import('./components/FreePlanBanner'));

const MainLayout = ({ children }) => {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <Navbar />
      <React.Suspense fallback={null}><ExpiryBanner /></React.Suspense>
      <React.Suspense fallback={null}><FreePlanBanner /></React.Suspense>
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Admin Protected Route wrapper
const AdminProtectedRoute = ({ children }) => {
  const { isAdmin, loading } = useSuperAdmin();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-600 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// App Content with Routes
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">جاري التحميل...</p>
        </div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Patient Portal Routes (Public) */}
      <Route path="/login" element={<PatientLogin />} />
      <Route path="/patient/qr/:token" element={<PatientQRLogin />} />
      <Route path="/patient/results" element={<PatientResults />} />
      <Route path="/patient/report/:bookingId" element={<MedicalReport />} />
      
      {/* Contractor Portal Routes */}
      <Route path="/contractor/login" element={<ContractorLogin />} />
      <Route path="/contractor/dashboard" element={<ContractorDashboard />} />
      <Route path="/contractor/statement" element={<ContractorStatement />} />
      <Route path="/contractor/report/:bookingId" element={<MedicalReport />} />
      
      {/* Payment & Subscription Routes (Public) */}
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/subscription-expired" element={<SubscriptionExpired />} />

      {/* Onboarding Routes (Public) — new checkout-first flow. The old
          Register page is kept only as a redirect so legacy links still work. */}
      <Route path="/register" element={<Navigate to="/onboarding" replace />} />
      <Route path="/register-legacy" element={<Register />} />
      <Route path="/onboarding" element={<OnboardingFlow />} />
      <Route path="/onboarding/start" element={<Navigate to="/onboarding" replace />} />
      <Route path="/onboarding/setup" element={<SetupWizard />} />

      {/* Staff Login Route */}
      <Route
        path="/staff-login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />

      {/* Auth aux routes (public) — magic link + forgot/reset flows. */}
      <Route path="/auto-login" element={<AutoLogin />} />
      <Route path="/force-change-password" element={<ForceChangePassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public landing pages */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />

      {/* Landing page for unauthenticated users, dashboard for authenticated */}
      <Route path="/" element={
        isAuthenticated
          ? <ProtectedRoute><MainLayout><HomeRoute /></MainLayout></ProtectedRoute>
          : <LandingPage />
      } />
      
      {/* Customers */}
      <Route path="/customers" element={
        <ProtectedRoute>
          <MainLayout><CustomersList /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/customers/new" element={
        <ProtectedRoute>
          <MainLayout><NewCustomer /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Bookings */}
      <Route path="/bookings" element={
        <ProtectedRoute>
          <MainLayout><BookingsList /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings/home-visits" element={
        <ProtectedRoute>
          <MainLayout><HomeVisitsList /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings/new" element={
        <ProtectedRoute>
          <MainLayout><NewBooking /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings/:id" element={
        <ProtectedRoute>
          <MainLayout><BookingDetails /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings/:id/work-request" element={
        <ProtectedRoute>
          <WorkRequest />
        </ProtectedRoute>
      } />
      <Route path="/bookings/:id/invoice" element={
        <ProtectedRoute>
          <Invoice />
        </ProtectedRoute>
      } />
      <Route path="/bookings/approve" element={
        <ProtectedRoute>
          <MainLayout><ApproveResults /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings/contractor" element={
        <ProtectedRoute featureKey="contractor_labs">
          <MainLayout><ContractorBookingsList /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/lab/report/:bookingId" element={
        <ProtectedRoute>
          <MedicalReport />
        </ProtectedRoute>
      } />
      
      {/* Lab Operations */}
      <Route path="/lab/results" element={
        <ProtectedRoute>
          <MainLayout><LabResults /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/lab/analysis" element={
        <ProtectedRoute featureKey="results_analysis">
          <MainLayout><ResultsAnalysis /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/lab/analysis/patient/:customerId" element={
        <ProtectedRoute featureKey="results_analysis">
          <MainLayout><PatientProfileAnalysis /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/lab/chat" element={
        <ProtectedRoute featureKey="lab_chat">
          <MainLayout><LabChat /></MainLayout>
        </ProtectedRoute>
      } />

      {/* Quality Control (QC) */}
      <Route path="/qc/materials" element={
        <ProtectedRoute pageKey="qc_materials" featureKey="qc_module">
          <MainLayout><QCMaterialsSetup /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/qc/daily-entry" element={
        <ProtectedRoute pageKey="qc_daily_entry" featureKey="qc_module">
          <MainLayout><QCDailyEntry /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/qc/dashboard" element={
        <ProtectedRoute pageKey="qc_dashboard" featureKey="qc_module">
          <MainLayout><QCDashboard /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Tests */}
      <Route path="/tests" element={
        <ProtectedRoute>
          <MainLayout><AllTests /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tests/categories" element={
        <ProtectedRoute>
          <MainLayout><TestCategories /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tests/packages" element={
        <ProtectedRoute featureKey="test_packages">
          <MainLayout><TestPackages /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tests/microbiology-dictionary" element={
        <ProtectedRoute>
          <MainLayout><MicrobiologyDictionary /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tests/contractor-prices" element={
        <ProtectedRoute>
          <MainLayout><ContractedLabPrices /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tests/pricing" element={
        <ProtectedRoute>
          <MainLayout><TestPricing /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tests/contractor-prices" element={
        <ProtectedRoute>
          <MainLayout><PlaceholderPage title="أسعار المختبرات المتعاقدة" icon={Building2} description="إدارة أسعار المختبرات الخارجية" /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Reports */}
      <Route path="/reports/income" element={
        <ProtectedRoute pageKey="income_report">
          <MainLayout><IncomeReport /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/daily" element={
        <ProtectedRoute pageKey="daily_income">
          <MainLayout><DailyIncomeReport /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/sales" element={
        <ProtectedRoute pageKey="sales_analysis">
          <MainLayout><SalesAnalysis /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/zatca-log" element={
        <ProtectedRoute featureKey="zatca_integration">
          <MainLayout><ZatcaInvoiceLog /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Marketing */}
      <Route path="/marketing/coupons" element={
        <ProtectedRoute pageKey="coupons" featureKey="coupons">
          <MainLayout><Coupons /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing/reviews" element={
        <ProtectedRoute>
          <MainLayout><PlaceholderPage title="مراجعات العملاء" icon={Star} description="عرض وإدارة تقييمات العملاء" /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing/whatsapp" element={
        <ProtectedRoute pageKey="whatsapp_marketing" featureKey="whatsapp_marketing">
          <MainLayout><WhatsappMarketing /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Settings - Permission based */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <MainLayout><PlaceholderPage title="إعدادات المختبر" icon={Wrench} description="إعدادات النظام العامة" /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/staff" element={
        <ProtectedRoute pageKey="staff_management">
          <MainLayout><StaffManagement /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/lab" element={
        <ProtectedRoute pageKey="lab_settings">
          <MainLayout><LabSettings /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/devices" element={
        <ProtectedRoute pageKey="devices" featureKey="device_integration">
          <MainLayout><DevicesManagement /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/analyzer-mappings" element={
        <ProtectedRoute pageKey="analyzer_mappings" featureKey="device_integration">
          <MainLayout><AnalyzerMappings /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/branches" element={
        <ProtectedRoute pageKey="branches">
          <MainLayout><BranchesManagement /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/contractor-labs" element={
        <ProtectedRoute featureKey="contractor_labs">
          <MainLayout><ContractorLabsManagement /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/contractor-accounts" element={
        <ProtectedRoute featureKey="contractor_labs">
          <MainLayout><ContractorAccounts /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/insurance" element={
        <ProtectedRoute featureKey="insurance_companies">
          <MainLayout><PlaceholderPage title="شركات التأمين" icon={Shield} description="إدارة شركات التأمين المتعاقدة" /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/subscription" element={
        <ProtectedRoute>
          <MainLayout><MySubscription /></MainLayout>
        </ProtectedRoute>
      } />
      {/* Both upgrade & renew now run through the same multi-step receipt
          flow as new-account onboarding (Plan → Devices → Checkout →
          Receipt → Pending → Activated). The component renders without
          MainLayout — it's a full-screen flow like onboarding, not a
          settings sub-page. */}
      <Route path="/settings/subscription/upgrade" element={
        <ProtectedRoute>
          <SubscriptionChangeFlow />
        </ProtectedRoute>
      } />
      <Route path="/settings/subscription/renew" element={
        <ProtectedRoute>
          <SubscriptionChangeFlow />
        </ProtectedRoute>
      } />

      {/* Per-Tenant Marketing Website — Phase 1 scaffolding */}
      {/* All gated by has_website feature; DomainManagement is accessible without
          has_custom_domain so the UI can upsell the add-on / upgrade. */}
      <Route path="/settings/website" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><WebsiteRouter /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/site-settings" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><SiteSettings /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/seo" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><SEOSettings /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/media" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><MediaLibrary /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/blog" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><BlogManagement /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/blog/:id" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><BlogPostEditor /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/offers" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><OfferManagement /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/offers/:id" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><OfferEditor /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/domain" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><DomainManagement /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings/website/messages" element={
        <ProtectedRoute featureKey="has_website">
          <MainLayout><ContactInbox /></MainLayout>
        </ProtectedRoute>
      } />

      {/* Super Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={
        <AdminProtectedRoute>
          <AdminLayout />
        </AdminProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="tenants" element={<TenantsList />} />
        <Route path="tenants/:id" element={<TenantDetails />} />
        <Route path="plans" element={<PlanManagement />} />
        <Route path="subscriptions" element={<SubscriptionsList />} />
        <Route path="payments" element={<PaymentHistory />} />
        <Route path="payment-approvals" element={<PaymentApprovals />} />
        <Route path="discount-codes" element={<DiscountCodes />} />
        <Route path="audit" element={<AuditLog />} />
        <Route path="zatca" element={<ZatcaOverview />} />
        <Route path="templates" element={<TemplateManagement />} />
        <Route path="admins" element={<AdminsManagement />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <SuperAdminProvider>
        <AuthProvider>
          <BranchProvider>
            <TenantBrandingProvider>
              <SubscriptionProvider>
                <Toaster
                  position="top-center"
                  toastOptions={{
                    duration: 3000,
                    style: {
                      background: '#fff',
                      color: '#333',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                    },
                  }}
                />
                <AppRoutes />
              </SubscriptionProvider>
            </TenantBrandingProvider>
          </BranchProvider>
        </AuthProvider>
      </SuperAdminProvider>
    </BrowserRouter>
  );
}

export default App;
